import { UUID } from 'crypto';
import { AdjustedCargoTemperature } from 'src/types/ViewModel/Stowage/AdjustedCargoTemperature';
import { BlockedTank } from 'src/types/ViewModel/Stowage/BlockedTank';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { isNullOrUndefined } from 'src/utils/objectUtils';
import { getBerthCalls, isManualCall } from '../stowageRotationUtils';
import { calculatePropertySum } from 'src/utils/arrayUtils';

export type RotationElement = RotationPortCall | RotationBerthCall | RotationCargoOperation | RotationCargo;
export type RotationItem = { rotationElement: RotationElement; extraData?: any };
export type StowageDataChangeElement = Stowage | AdjustedCargoTemperature | BlockedTank;
export type StowageDataChangeItem = { stowageDataElement: StowageDataChangeElement; extraData?: any };

export const findByPortCallMergeProperties = (incomingPortCall: RotationPortCall, existingRotation: RotationPortCall[]): RotationPortCall => {
    const matchingPortCalls = existingRotation.filter(
        (item) =>
            isManualCall(item) &&
            item.portId === incomingPortCall.portId &&
            item.voyageNumber === incomingPortCall.voyageNumber &&
            item.portCallStatus === incomingPortCall.portCallStatus
    );

    if (matchingPortCalls.length === 0) {
        return null;
    }

    // Find the port call matching the sequence location (incoming port call from IMOS to be merged may not have same port order number as manual port call)
    const matchingPortCall = matchingPortCalls.find((item) => {
        // Find the port call in the existing rotation with the highest port order number but still lower than the item's port order number (within the voyage)
        const previousPortCall = existingRotation
            .filter((rpc) => rpc.voyageNumber === item.voyageNumber)
            .reduce((prev, curr) => {
                if (curr.portOrderNumber < item.portOrderNumber) {
                    return curr;
                }
                return prev;
            }, null);

        const nextPortCall = existingRotation
            .filter((rpc) => rpc.voyageNumber === item.voyageNumber)
            .reduce((next, curr) => {
                if (curr.portOrderNumber > item.portOrderNumber) {
                    return curr;
                }
                return next;
            }, null);

        // If the incomingPortCall is between the previous and next port calls, or if it is the first or last port call, return the item
        if (
            (previousPortCall &&
                nextPortCall &&
                previousPortCall.portOrderNumber < incomingPortCall.portOrderNumber &&
                incomingPortCall.portOrderNumber < nextPortCall.portOrderNumber) ||
            (!previousPortCall && nextPortCall && incomingPortCall.portOrderNumber < nextPortCall.portOrderNumber) ||
            (previousPortCall && !nextPortCall && previousPortCall.portOrderNumber < incomingPortCall.portOrderNumber)
        ) {
            return true;
        }
    });

    return matchingPortCall || null;
};

export const findByBerthCallMergeProperties = (incomingBerthCall: RotationBerthCall, existingRotation: RotationPortCall[]): RotationBerthCall =>
    existingRotation
        .flatMap((item) => getBerthCalls(item))
        .find(
            (item) =>
                isManualCall(item) &&
                item.berthId === incomingBerthCall.berthId &&
                ((isNullOrUndefined(item.berthSequenceNumber) && incomingBerthCall.berthSequenceNumber === 0) ||
                    item.berthSequenceNumber === incomingBerthCall.berthSequenceNumber) &&
                item.portCallGuid === incomingBerthCall.portCallGuid
        );

export const findByCargoOperationMergeProperties = (
    incomingCargoOperation: RotationCargoOperation,
    existingRotation: RotationPortCall[],
    transitOperations: RotationPortCall[]
): RotationCargoOperation | null => {
    const _filteredCargoOperations = existingRotation
        .flatMap((item) => getBerthCalls(item))
        .flatMap((item) => item.cargoOperations)
        .filter(
            (item) =>
                isManualCall(item) &&
                item.berthCallGuid === incomingCargoOperation.berthCallGuid &&
                item.portCallGuid === incomingCargoOperation.portCallGuid &&
                item.operationType === incomingCargoOperation.operationType &&
                item.cargoOperationSequenceNumber === incomingCargoOperation.cargoOperationSequenceNumber &&
                item.cargoGuid === incomingCargoOperation.cargoGuid
        );

    if (_filteredCargoOperations.length !== 1) return null;

    const _transitWeight = transitOperations
        .filter((item) => item.portCallGuid === incomingCargoOperation.portCallGuid)
        .flatMap((item) => item.berthCalls)
        .flatMap((item) => item.cargoOperations)
        .filter((co) => co.cargoGuid === incomingCargoOperation.cargoGuid)
        .reduce((sum, co) => sum + (co.nominatedWeight || 0), 0);

    const _totalWeight = _filteredCargoOperations[0].nominatedWeight + _transitWeight;

    return _totalWeight === incomingCargoOperation.nominatedWeight ? _filteredCargoOperations[0] : null;
};

export const hasMatchingSpiltCargoOperations = (
    incomingCargoOperation: RotationCargoOperation,
    existingRotation: RotationPortCall[],
    transitOperations: RotationPortCall[]
): boolean => {
    const existingCargoOperations = existingRotation
        .flatMap((portCall) => getBerthCalls(portCall))
        .flatMap((berthCall) => berthCall.cargoOperations)
        .filter(
            (cargoOperation) =>
                isManualCall(cargoOperation) &&
                cargoOperation.berthCallGuid === incomingCargoOperation.berthCallGuid &&
                cargoOperation.portCallGuid === incomingCargoOperation.portCallGuid &&
                cargoOperation.operationType === incomingCargoOperation.operationType &&
                cargoOperation.cargoGuid === incomingCargoOperation.cargoGuid
        );

    if (existingCargoOperations.length === 0) return false;

    transitOperations = transitOperations ?? [];
    const transitCargoOperations = transitOperations
        .filter((portCall) => portCall.portCallGuid === incomingCargoOperation.portCallGuid)
        .flatMap((portCall) => portCall.berthCalls)
        .flatMap((berthCall) => berthCall.cargoOperations)
        .filter((cargoOperation) => cargoOperation.cargoGuid === incomingCargoOperation.cargoGuid);

    const totalNominatedWeight =
        calculatePropertySum(existingCargoOperations, 'nominatedWeight') + calculatePropertySum(transitCargoOperations, 'nominatedWeight');

    return totalNominatedWeight === incomingCargoOperation.nominatedWeight;
};

export const findByCargoMergeProperties = (incomingCargo: RotationCargo, cargoes: RotationCargo[]): RotationCargo =>
    cargoes.find(
        (item) =>
            isManualCall(item) &&
            item.productId === incomingCargo.productId &&
            item.cargoNumber === incomingCargo.cargoNumber &&
            incomingCargo.voyageNumber === item.voyageNumber
    );

/**
 * Find a cargo operation that can be merged with the given cargo operation.
 * A mergeable cargo operation is an IMOS cargo operation that matches a manual cargo operation
 * by berth call, port call, operation type, and cargo.
 *
 * @param cargoOperation - The cargo operation to find a merge candidate for
 * @param allCargoOperations - All cargo operations in the stowage plan
 * @returns The mergeable cargo operation, or null if none found
 */
export const findMergeableCargoOperation = (
    cargoOperation: RotationCargoOperation,
    allCargoOperations: RotationCargoOperation[]
): RotationCargoOperation | null => {
    if (!cargoOperation || !allCargoOperations?.length) return null;

    const isManual = isManualCall(cargoOperation);

    // Find a matching cargo operation with opposite data source
    const mergeCandidate = allCargoOperations.find(
        (op) =>
            op.cargoOperationGuid !== cargoOperation.cargoOperationGuid &&
            isManualCall(op) !== isManual && // One must be manual, one must be IMOS
            op.berthCallGuid === cargoOperation.berthCallGuid &&
            op.portCallGuid === cargoOperation.portCallGuid &&
            op.operationType === cargoOperation.operationType &&
            op.cargoGuid === cargoOperation.cargoGuid
    );

    return mergeCandidate ?? null;
};

/**
 * Find a cargo that can be merged with the given cargo.
 * A mergeable cargo is an IMOS cargo that matches a manual cargo
 * by product ID, cargo number, and voyage number.
 *
 * @param cargo - The cargo to find a merge candidate for
 * @param allCargoes - All cargoes in the stowage plan
 * @returns The mergeable cargo, or null if none found
 */
export const findMergeableCargo = (cargo: RotationCargo, allCargoes: RotationCargo[]): RotationCargo | null => {
    if (!cargo || !allCargoes?.length) return null;

    const isManual = isManualCall(cargo);

    // Find a matching cargo with opposite data source
    const mergeCandidate = allCargoes.find(
        (c) =>
            c.cargoGuid !== cargo.cargoGuid &&
            isManualCall(c) !== isManual && // One must be manual, one must be IMOS
            c.productId === cargo.productId &&
            c.cargoNumber === cargo.cargoNumber &&
            c.voyageNumber === cargo.voyageNumber
    );

    return mergeCandidate ?? null;
};

export const findByPortCallId = (portCallId: number | UUID, list: RotationPortCall[]): RotationPortCall =>
    list.find((item) => (isNullOrUndefined(item.imosPortCallId) ? item.portCallGuid === portCallId : item.imosPortCallId === portCallId));

export const findByBerthCallId = (berthCallId: number | UUID, list: RotationPortCall[]): RotationBerthCall =>
    list
        .flatMap((item) => getBerthCalls(item))
        .find((item) => (isNullOrUndefined(item.imosBerthCallId) ? item.berthCallGuid === berthCallId : item.imosBerthCallId === berthCallId));

export const findByCargoOperationId = (cargoOperationId: number | UUID, list: RotationPortCall[]): RotationCargoOperation =>
    list
        .flatMap((item) => getBerthCalls(item))
        .flatMap((item) => item.cargoOperations)
        .find((item) =>
            isNullOrUndefined(item.imosCargoOperationId)
                ? item.cargoOperationGuid === cargoOperationId
                : item.imosCargoOperationId === cargoOperationId
        );

export const findByCargoId = (cargoId: number | UUID, cargoes: RotationCargo[]): RotationCargo =>
    cargoes.find((item) => (isNullOrUndefined(item.imosCargoId) ? item.cargoGuid === cargoId : item.imosCargoId === cargoId));

export const getItemImosId = (stowageChangeDataType: StowageChangeDataType, item: RotationElement): number | UUID | null => {
    switch (stowageChangeDataType) {
        case StowageChangeDataType.RotationPortCall:
            return (item as RotationPortCall).imosPortCallId ?? (item as RotationPortCall).portCallGuid;
        case StowageChangeDataType.RotationBerthCall:
            return (item as RotationBerthCall).imosBerthCallId ?? (item as RotationBerthCall).berthCallGuid;
        case StowageChangeDataType.RotationCargoOperation:
            return (item as RotationCargoOperation).imosCargoOperationId ?? (item as RotationCargoOperation).cargoOperationGuid;
        case StowageChangeDataType.RotationCargo:
            return (item as RotationCargo).imosCargoId ?? (item as RotationCargo).cargoGuid;
    }
};

export const getItems = (rotation: RotationPortCall[] | RotationCargo[], stowageChangeDataType: StowageChangeDataType): RotationItem[] => {
    switch (stowageChangeDataType) {
        case StowageChangeDataType.RotationPortCall:
        case StowageChangeDataType.RotationCargo:
            return rotation.map((item) => ({
                rotationElement: item,
                extraData: {},
            }));
        case StowageChangeDataType.RotationBerthCall:
            return rotation.flatMap((portCall) => getBerthCalls(portCall).map((berthCall) => ({ rotationElement: berthCall, extraData: {} })));
        case StowageChangeDataType.RotationCargoOperation:
            return rotation.flatMap((portCall) =>
                getBerthCalls(portCall).flatMap((berthCall) =>
                    berthCall.cargoOperations.map((cargoOperation) => ({
                        rotationElement: cargoOperation,
                        extraData: {},
                    }))
                )
            );
        default:
            return [];
    }
};
